import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

const STATUS_LABELS: Record<string, () => string> = {
  active: () => m.status_active(),
  approved: () => m.status_approved(),
  archived: () => m.status_archived(),
  at_origin: () => m.status_at_origin(),
  awaiting_inventory: () => "Awaiting Inventory",
  held_for_customer: () => "Held for customer",
  cancelled: () => m.status_cancelled(),
  converted: () => m.status_converted(),
  delivered: () => m.status_delivered(),
  expired: () => m.status_expired(),
  featured: () => m.status_featured(),
  in_spot: () => m.status_in_spot(),
  in_transit: () => m.status_in_transit(),
  local_pickup: () => m.delivery_method_local_pickup(),
  on_hold: () => m.status_on_hold(),
  paid: () => m.status_paid(),
  payment_processing: () => m.status_payment_processing(),
  pending: () => m.status_pending(),
  pending_payment: () => m.status_pending_payment(),
  ready_for_pickup: () => m.status_ready_for_pickup(),
  ready_to_prepare: () => m.status_ready_to_pull(),
  ready_to_pull: () => m.status_ready_to_pull(),
  rejected: () => m.status_rejected(),
  released: () => m.status_released(),
  requested: () => m.status_requested(),
  shipped: () => m.status_shipped(),
  sold_out: () => m.status_sold_out(),
  upcoming: () => m.status_upcoming(),
};

function resolveLocale() {
  try {
    return getLocale();
  } catch {
    return "en";
  }
}

/**
 * Parse `yyyy-MM-dd` as local start-of-day. Required because `new Date("yyyy-MM-dd")` alone is UTC
 * midnight in ECMAScript, which shifts the calendar day for local display (e.g. US timezones).
 * Matches the internal DatePicker's `value + "T00:00:00"` parsing.
 */
export function dateInputStringToLocalStartOfDayMs(dateStr: string): number | undefined {
  const trimmed = dateStr.trim();
  if (!trimmed) return undefined;
  const ms = new Date(`${trimmed}T00:00:00`).getTime();
  return Number.isFinite(ms) ? ms : undefined;
}

/**
 * Parse `yyyy-MM-dd` as the last millisecond of that local day (start of the next
 * local day minus 1ms). Uses calendar arithmetic rather than `start + 24h` so it
 * stays correct across DST transitions, where a local day is 23 or 25 hours.
 */
export function dateInputStringToLocalEndOfDayMs(dateStr: string): number | undefined {
  const trimmed = dateStr.trim();
  if (!trimmed) return undefined;
  const start = new Date(`${trimmed}T00:00:00`);
  if (Number.isNaN(start.getTime())) return undefined;
  const nextDayStart = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + 1,
  );
  return nextDayStart.getTime() - 1;
}

/** Inverse of {@link dateInputStringToLocalStartOfDayMs} for form fields and DatePicker `value`. */
export function timestampToLocalDateInputString(ts: number): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Format date-only timestamps that are persisted at UTC midnight (`Date.UTC(y, m, d)`).
 * This keeps the calendar day stable for all user timezones when rendering shipping/pickup dates.
 */
export function formatUtcDateOnly(
  date: Date | string | number | undefined,
  opts: Intl.DateTimeFormatOptions = {},
) {
  if (!date) return "";

  try {
    return new Intl.DateTimeFormat(resolveLocale(), {
      month: opts.month ?? "long",
      day: opts.day ?? "numeric",
      year: opts.year ?? "numeric",
      ...opts,
      timeZone: "UTC",
    }).format(new Date(date));
  } catch (_err) {
    return "";
  }
}

/** Convert a UTC-midnight date-only timestamp to a stable `yyyy-MM-dd` key. */
export function timestampToUtcDateKey(ts: number): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Resolve inclusive UTC day bounds for a `yyyy-MM-dd` key. */
export function utcDateKeyToDayBounds(dateKey: string): { start: number; end: number } | null {
  const match = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const start = Date.UTC(year, month, day, 0, 0, 0, 0);
  const end = Date.UTC(year, month, day, 23, 59, 59, 999);
  const parsed = new Date(start);

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return { start, end };
}

export function formatDate(
  date: Date | string | number | undefined,
  opts: Intl.DateTimeFormatOptions = {},
) {
  if (!date) return "";

  try {
    return new Intl.DateTimeFormat(resolveLocale(), {
      month: opts.month ?? "long",
      day: opts.day ?? "numeric",
      year: opts.year ?? "numeric",
      ...opts,
    }).format(new Date(date));
  } catch (_err) {
    return "";
  }
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(resolveLocale(), {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatRelativeTime(timestamp: number): string {
  const delta = Date.now() - timestamp;
  const formatter = new Intl.RelativeTimeFormat(resolveLocale(), {
    numeric: "auto",
  });
  const inDays = Math.round(delta / (24 * 60 * 60 * 1000));
  if (Math.abs(inDays) >= 1) {
    return formatter.format(-inDays, "day");
  }
  const inHours = Math.round(delta / (60 * 60 * 1000));
  if (Math.abs(inHours) >= 1) {
    return formatter.format(-inHours, "hour");
  }
  const inMinutes = Math.round(delta / (60 * 1000));
  return formatter.format(-inMinutes, "minute");
}

/**
 * Enterprise "pay manually later" scheduled payments store a ~100-year sentinel
 * `scheduledDate` meaning "no fixed due date" (see backend `checkout.ts` /
 * `enterpriseCheckout.ts`). Anything more than 50 years out is that sentinel and
 * must never be rendered as a real due/scheduled date.
 */
export function isPayWhenReadySentinelDate(timestamp: number): boolean {
  return timestamp - Date.now() > 50 * 365 * 24 * 60 * 60 * 1000;
}

export function formatStatusLabel(
  status?: string | null,
  defaultLabel: string = m.status_unknown(),
): string {
  if (!status) return defaultLabel;

  const normalizedKey = status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const translated = STATUS_LABELS[normalizedKey];
  if (translated) return translated();

  const normalized = normalizedKey.replace(/_/g, " ");
  if (!normalized) return defaultLabel;

  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function formatDeliveryMethod(
  method?: string | null,
  defaultLabel: string = "-",
): string {
  if (!method) return defaultLabel;

  if (method.trim().toLowerCase().replace(/[\s-]+/g, "_") === "local_pickup") {
    return m.delivery_method_local_pickup();
  }

  return formatStatusLabel(method, defaultLabel);
}
